export default class Component {
    constructor(appConfig) {
        const {
            styles, scripts, element, markup, productPageInfo, appFactory, mountPoints, language, analytics, resources, parent,
        } = appConfig;

        // CSS styles for the app.
        this.styles = styles;

        // Does nothing currently.
        this.scripts = scripts;

        // Provided HTML
        this.markup = markup;

        // Details about the page (ProductId, retailer, etc)
        this.productPageInfo = productPageInfo;

        // MountDetails (Contains each area and details for that mount)
        this.mountPoints = mountPoints;

        // AppFactory Reference (If needed, not sure why you would)
        this.appFactory = appFactory;

        // App Key within the config. Should have the same class name
        this.appConfig = this.productPageInfo.retailerConfig.apps[this.constructor.name];

        // Root element
        this.element = element;

        // Parent Apps
        this.parent = parent;

        // Element copies.
        this.elementCopies = [];

        // Dynamic URL Resources (Logos, pictures etc)
        this.resources = resources;

        // Languages
        this.language = language;

        // App Analytics
        this.analytics = analytics;

        // Connections to the app.
        this.connections = {};

        // App Extension / overrides
        this.overrides = {};

        // Arguments on the mount point.
        this.parentalArguments = {};

        // Sentry
        this.sentry = appFactory.masterConfig.ENVIRONMENT.SENTRY.client;

        // Is this app of valid construction?
        // Follows the hook model?
        this.validateComponentImplementation(this);

        // Apply overrides / extension
        this.applyOverrides();
    }

    validateComponentImplementation(component) {
        const requiredFunctions = ['init', 'beforeMountHook', 'preRenderHook', 'postRenderHook', 'overrideHook', 'firstInteractionHook'];
        for (const funcName of requiredFunctions) {
            if (typeof component[funcName] !== 'function') {
                throw new Error(`Component implementation is not valid. The function "${funcName}" is missing.`);
            }
        }
    }

    async waitForElementToExist(selector, selectionRoot = document, timeout = 1000) {
        return new Promise((resolve, reject) => {
            const startTime = performance.now(); // Use performance.now() for better precision

            const observeBody = () => {
                const observer = new MutationObserver((mutationsList, watcher) => {
                    const element = selectionRoot.querySelector(selector);
                    if (element) {
                        watcher.disconnect();
                        resolve(element);
                    } else if (performance.now() - startTime > timeout) {
                        watcher.disconnect();
                        reject(new Error(`Timeout exceeded while waiting for element with selector "${selector}"`));
                    }
                });

                observer.observe(selectionRoot, {
                    childList: true,
                    subtree: true,
                });

                // Check for the element initially
                const initialElement = selectionRoot.querySelector(selector);
                if (initialElement) {
                    observer.disconnect();
                    resolve(initialElement);
                }
            };

            // god this was a pain in the ass.....
            if (selectionRoot === document && !document.body) {
                // If the selectionRoot is document and document.body doesn't exist yet,
                // wait for it to become available before observing mutations
                document.addEventListener('DOMContentLoaded', observeBody);
            } else {
                // Otherwise, observe immediately
                observeBody();
            }
        });
    }

    deepClone(host) {
        const cloneNode = (node, parent) => {
            const walkTree = (nextn, nextp) => {
                while (nextn) {
                    cloneNode(nextn, nextp);
                    nextn = nextn.nextSibling;
                }
            };

            const clone = node.cloneNode();
            parent.appendChild(clone);
            if (node.shadowRoot) {
                walkTree(node.shadowRoot.firstChild, clone.attachShadow({ mode: 'open' }));
            }

            walkTree(node.firstChild, clone);
        };

        const fragment = document.createDocumentFragment();
        cloneNode(host, fragment);
        return fragment;
    }

    insertCSS(styles, insertionPoint = null) {
        const { shadowRoot } = this.element;
        for (const style of styles) {
            const styleElement = document.createElement('style');
            styleElement.textContent = style;

            if (insertionPoint) {
                const targetElement = shadowRoot.querySelector(insertionPoint);
                if (targetElement) {
                    targetElement.appendChild(styleElement);
                }
            } else {
                shadowRoot.appendChild(styleElement);
            }
        }
    }

    insertHTML(markupFiles, insertionPoint = null) {
        const { shadowRoot } = this.element;
        const parser = new DOMParser();

        for (const htmlText of markupFiles) {
            const parsedHTML = parser.parseFromString(htmlText, 'text/html');

            if (insertionPoint) {
                const targetElement = shadowRoot.querySelector(insertionPoint);
                if (targetElement) {
                    // Append each element from parsedHTML to the shadow DOM
                    parsedHTML.body.childNodes.forEach((node) => {
                        shadowRoot.appendChild(node.cloneNode(true));
                    });
                }
            } else {
                // If no insertion point specified, simply append the HTML elements to the shadow DOM
                parsedHTML.body.childNodes.forEach((node) => {
                    shadowRoot.appendChild(node.cloneNode(true));
                });
            }
        }
    }

    insertLanguages(langs) {
        // Dynamic insertion using a recursive post order
        // technique is the default function to insertion of languages
        // feel free to override.
        this.recursiveLanguageInsertion(langs, '');
        return true;
    }

    mountElementToPoints() {
        for (const domPoint of this.mountPoints) {
            const targetElement = document.querySelector(domPoint);
            if (targetElement) {
                targetElement.appendChild(this.element);
            }
        }
    }

    async runFunctionOnAllCopies(func) {
        for (const element of this.elementCopies) {
            const root = element.shadowRoot;
            func(root);
        }
    }

    handleError(error) {
        debugger;
        this.sentry.captureException(error);
        console.error('Critical Error:', error);
    }

    recursiveLanguageInsertion(obj, currentKeyChain, component = this.element) {
        // This is the dynamic recursive language insertion system.
        // It maps the structure of a JSON object directly to a DOM.
        // Where the Keys in language JSON represent data attributes
        // in the DOM
        // <P title> PLACEHOLDER </P>
        // Using a Post Order traversal technique, we grab the keys
        // in the JSON document and apply them to the dom if they match.

        // Example:
        // {"app": {"title": 'test'}}.
        // <div app> <p title> placeholder</p> </div>
        // will become:
        // <div app> <p title> test</p> </div>
        // because it matched this: x.querySelect('[app] [title]')

        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                const keyChain = currentKeyChain ? `${currentKeyChain} [${key}]` : `[${key}]`;
                const translation = obj[key];
                const elements = component.shadowRoot.querySelectorAll(keyChain);

                for (const element of elements) {
                    if (element && typeof translation === 'string') {
                        element.innerText = translation;
                    }
                }

                if (typeof translation === 'object') {
                // If the translation is an object, continue recursively
                    this.recursiveLanguageInsertion(translation, keyChain, component);
                }
            }
        }
        return true;
    }

    // By default, we use shadowboxed elements where it's root
    // is the section tag. But we can also have ghost elements. Who
    // is rooted by itself.
    getElementRoot(element) {
        if (element.shadowRoot) {
            return element.shadowRoot.querySelector('section');
        }
        return element;
    }

    // A ghost element, is an element that already exists
    // we just wish to return it's references. Often times to attach
    // events too.
    mountGhostElements(mountPoint, selectionRoot = document) {
        const ghostElements = [];
        const targetElements = selectionRoot.querySelectorAll(mountPoint);
        if (targetElements.length > 0) {
            targetElements.forEach((targetElement) => {
                ghostElements.push(targetElement);
            });
        }
        return ghostElements;
    }

    mountMultipleElementsToPoints(mountPoint, selectionRoot = document) {
        const newElements = [];
        const targetElements = selectionRoot.querySelectorAll(mountPoint);
        if (targetElements.length > 0) {
            targetElements.forEach((targetElement) => {
                const clone = this.deepClone(this.element);
                targetElement.appendChild(clone);
                this.elementCopies.push(targetElement.lastElementChild);
                newElements.push(targetElement.lastElementChild);
            });
        }
        return newElements;
    }

    // Default Component mounter.
    // Sdom & non sdom
    // Async await for parallel mounting on all points.
    // Arguments and parental arguments (key values)
    // Ghost mounting.
    async mountComponentToPoints() {
        // God this was hard to make,
        // but now it will parallelize all the mounting.
        // as opposed to waiting for each one, do all at once.
        let counter = 0;

        const insertPromises = [];

        for (const point of this.mountPoints) {
            try {
                // Deconstruct mountPoint Details
                const mountPoints = point.mount_points;

                // Create a function to insert elements for a single mount point
                // eslint-disable-next-line no-loop-func
                const insertElementsForMountPoint = async (mountPoint) => {
                    try {
                        await this.waitForElementToExist(mountPoint);

                        // Apply the parental arguments to the parent of the
                        // mount point.
                        const parentArgs = point.parent_attributes ?? [];

                        for (const arg of parentArgs) {
                            const [name, value] = arg.split('=');
                            const elm = document.querySelector(mountPoint);
                            elm.setAttribute(name, value);
                        }

                        // Is the element a ghost element?
                        // Meaning, we don't wish to insert an element at the mountPoint,
                        // only mount the listeners to said point.
                        const ghostMount = point.ghost_mount;

                        // Insert into DOM based upon the mount-point.
                        let newElements = [];
                        if (!ghostMount) {
                            newElements = this.mountMultipleElementsToPoints(mountPoint);
                        } else {
                            newElements = this.mountGhostElements(mountPoint);
                        }
                        // Insert attributes into the new elements  (Non Ghost Mounts.)
                        newElements.forEach((elm) => {
                            const root = this.getElementRoot(elm);
                            root.setAttribute('mountId', point.mount_id);
                            root.setAttribute('language', this.productPageInfo.languageCode);
                            point.attributes.forEach((attr) => {
                                const [name, value] = attr.split('=');
                                root.setAttribute(name, value);
                            });
                        });

                        this.root.setAttribute('language', this.productPageInfo.languageCode);
                        this.root.setAttribute('mountPointIndex', counter);

                        // Any other logic required for this app.
                        // In this case, mount event listeners.
                        this.createEventListeners(newElements);

                        // Does it require any special logic for the CTA?
                        this.postElementMount(newElements);

                        counter += 1;
                    } catch (error) {
                        console.error(error);
                        // console.log(...Log.w(`${this.constructor.name}`, 'WARN', `Could not mount this element correctly ${error}`));
                        return true;
                    }
                    return true;
                };

                // Create an array of promises for insertElementsForMountPoint
                const insertPromisesForPoint = mountPoints.map((mountPoint) => insertElementsForMountPoint(mountPoint));

                // Add these promises to the main insertPromises array
                insertPromises.push(...insertPromisesForPoint);
            } catch (error) {
                console.error(`Could not mount these elements correctly  ${error}`);
                // console.log(...Log.w(`${this.constructor.name}`, 'ERROR', `Could not mount these elements correctly ${error}`));
            }
        }

        // Use Promise.all to insert elements for all mount points concurrently
        await Promise.all(insertPromises);

        console.log(`${this.constructor.name}`, 'LOG', `Mounted ${counter} elements`);
        return true;
    }

    overrideMethod(name, fn) {
        this.overrides[name] = fn;

        // ! New bind, may break.
        this[name] = fn.bind(this);
    }

    restoreMethod(name) {
        if (name in this.overrides) {
            this[name] = this.overrides[name];
            delete this.overrides[name];
        }
    }

    applyOverrides() {
        // Check if overrides exist for appFactory
        if (window.__PRIME_AI__.override[this.constructor.name]) {
            const appFactoryOverrides = window.__PRIME_AI__.override[this.constructor.name];
            for (const methodName in appFactoryOverrides) {
                if (appFactoryOverrides.hasOwnProperty(methodName)) {
                    this.overrideMethod(methodName, appFactoryOverrides[methodName]);
                }
            }
        }
    }
}
